import {
  BUSINESS_NAME,
  BUSINESS_SUMMARY,
  LEGAL_ENTITY,
  NO_PUBLIC_LOCATION,
  SERVICE_TERRITORY
} from '../lib/businessContact.js';

const UPDATED = '2026-10-14';

export const HOME_PAGE = {
  updated: UPDATED,
  path: '/',
  title: 'Commercial Lighting & RGBW Systems in South Florida | Avision Lighting',
  description:
    'Avision Lighting installs commercial LED, RGBW and DMX, sports, recreation, and architectural lighting in Palm Beach, Broward, and Miami-Dade counties. Qualifying large-scale specialty projects may be considered nationwide.',
  h1: 'Commercial & specialty lighting across South Florida',
  lede: `${BUSINESS_SUMMARY} The work is permanently installed lighting for facilities — not lamp sales and not event-light rental.`,
  localSupport: `Serving ${SERVICE_TERRITORY.primaryCounties.map(c => c.replace(' County', '')).join(', ')} counties.`
};

export const HOME_CATEGORIES = [
  {
    href: '/services/entertainment-rgbw-dmx',
    label: 'Explore RGBW & DMX Lighting',
    text: 'Permanent color, white-light scenes, zones, and DMX control for venues and recreation facilities.'
  },
  {
    href: '/services/commercial-led',
    label: 'Commercial LED Retrofits',
    text: 'Fixture audits, replacement layouts, and commissioning for existing commercial buildings.'
  },
  {
    href: '/services/sports-lighting',
    label: 'Sports Lighting Systems',
    text: 'Fields, courts, and gyms, including white play light and RGBW where a facility wants both.'
  },
  {
    href: '/services/architectural-lighting',
    label: 'Architectural & Hospitality Lighting',
    text: 'Facade, accent, restaurant, and hotel lighting specified for appearance and South Florida weather.'
  }
];

export const HOME_SECONDARY = [
  { href: '/services/warehouse-lighting', label: 'Warehouse lighting' },
  { href: '/services/recreation-lighting', label: 'Recreation lighting' },
  { href: '/services/lighting-controls', label: 'Lighting controls' },
  { href: '/projects/sky-zone-facade-lighting', label: 'Sky Zone facade project' }
];

export const CORE_PAGES = {
  home: HOME_PAGE,
  about: {
    updated: UPDATED,
    path: '/about',
    title: 'About Avision Lighting | South Florida Commercial Lighting',
    description:
      'Avision Lighting is the commercial lighting brand of Avision Construction LLC in West Palm Beach, led by Chief Visionary Officer Dr. Bert Jerome Smith, ASHSM.',
    h1: 'About Avision Lighting',
    lede: `${BUSINESS_NAME} is the commercial lighting brand of ${LEGAL_ENTITY.legalName}, a certified contracting firm based in ${LEGAL_ENTITY.locality}, ${LEGAL_ENTITY.region}. ${BUSINESS_SUMMARY}`,
    owner: {
      name: 'Dr. Bert Jerome Smith, ASHSM',
      role: 'Chief Visionary Officer',
      company: LEGAL_ENTITY.legalName,
      image: {
        src: '/images/team/jerome-smith.jpg',
        webp: '/images/team/jerome-smith.webp',
        alt: 'Dr. Bert Jerome Smith, ASHSM, Chief Visionary Officer of Avision Construction LLC',
        width: 819,
        height: 1024
      },
      intro: `Dr. Bert Jerome Smith is an accomplished executive, educator, and construction professional serving as the Chief Visionary Officer of ${LEGAL_ENTITY.legalName}, the parent company of ${BUSINESS_NAME}. Based in ${LEGAL_ENTITY.locality}, Florida, he guides the strategic growth, operational compliance, and safety standards of the organization.`,
      sections: [
        {
          heading: 'Professional posture & safety expertise',
          paragraphs: [
            'With a deep-seated commitment to workplace compliance and field safety, Dr. Smith holds the Associate Safety and Health Manager (ASHSM) designation. That specialization supports environmental health, risk management, and occupational hazard control — safeguarding field personnel and client assets across commercial and residential projects.'
          ]
        },
        {
          heading: 'Academic foundation & credentials',
          paragraphs: [
            'Dr. Smith’s leadership is backed by a multi-disciplinary academic background that bridges business management, instructional design, and advanced research. He describes himself as an educational enthusiast and treats ongoing professional development as a core business asset.'
          ],
          list: [
            'Doctor of Philosophy (PhD) — terminal research and academic degree',
            'Master of Arts (MATED) — education, training, and instructional methodology',
            'Bachelor of Science in Management (BSM) — Management and Organizational Development, University of Phoenix',
            'Specialized coursework — Public Administration and Government studies at CUNY John Jay'
          ]
        }
      ],
      closing: `By blending corporate strategy with field-tested safety credentials, Dr. Smith helps keep ${LEGAL_ENTITY.legalName} — and its lighting brand, ${BUSINESS_NAME} — agile, compliant, and competitive in Florida’s contracting market.`
    },
    paragraphs: [
      `${BUSINESS_NAME} is the commercial lighting brand of ${LEGAL_ENTITY.legalName}, an active Florida LLC (document ${LEGAL_ENTITY.documentNumber}, filed ${LEGAL_ENTITY.dateFiled}; name updated ${LEGAL_ENTITY.nameChangeFiled}).`,
      `Based in ${LEGAL_ENTITY.locality}, ${LEGAL_ENTITY.region}, the crew installs across ${SERVICE_TERRITORY.primaryCounties.join(', ')}. ${NO_PUBLIC_LOCATION}`,
      `Featured work includes entertainment and recreation lighting — among them a Sky Zone facade, interior color systems, party-room scenes, and ongoing fixture maintenance. The same company also delivers commercial LED retrofits, warehouse and sports lighting, architectural and hospitality installs, and lighting controls.`,
      `${SERVICE_TERRITORY.nationwideNote}`
    ]
  },
  services: {
    updated: UPDATED,
    path: '/services',
    title: 'Commercial Lighting Services in South Florida | Avision Lighting',
    description:
      'RGBW and DMX, LED retrofits, warehouse, sports, recreation, architectural, hospitality, and lighting controls from Avision Lighting in South Florida.',
    h1: 'Commercial lighting services',
    lede: `${BUSINESS_NAME} installs commercial lighting across ${SERVICE_TERRITORY.primaryCounties.join(', ')}. Each service below is a separate scope with its own page.`
  },
  gallery: {
    updated: UPDATED,
    path: '/gallery',
    title: 'Lighting Project Gallery | Avision Lighting',
    description:
      'Project photography and video from Avision Lighting, with labeled illustrations for capability examples.',
    h1: 'Project gallery',
    lede: 'Real project media links to case studies. Capability illustrations are labeled separately so you always know which images are installed work.'
  },
  serviceArea: {
    updated: UPDATED,
    path: '/service-area',
    title: 'South Florida Lighting Service Area | Avision Lighting',
    description:
      'Avision Lighting primarily installs in Palm Beach, Broward, and Miami-Dade counties from West Palm Beach. Qualifying large-scale commercial and specialty projects may be considered nationwide.',
    h1: 'South Florida service area',
    lede: `${BUSINESS_NAME} is based in ${LEGAL_ENTITY.locality} and primarily serves ${SERVICE_TERRITORY.primaryCounties.join(', ')}. ${NO_PUBLIC_LOCATION}`,
    heroSupport:
      'Based in West Palm Beach. The crew installs at your facility — there is no public showroom to visit.'
  },
  contact: {
    updated: UPDATED,
    path: '/contact',
    title: 'Request a Lighting Assessment | Avision Lighting',
    description:
      'Request a lighting assessment from Avision Lighting for a commercial, RGBW, retrofit, sports, or architectural project in South Florida.',
    h1: 'Request a lighting assessment',
    lede:
      'Share the facility type, the county, and what the lighting has to do. If online delivery is unavailable, the phone numbers on this page are the working contact path.'
  },
  projects: {
    updated: UPDATED,
    path: '/projects',
    title: 'Commercial Lighting Projects | Avision Lighting',
    description:
      'Documented Avision Lighting projects: Sky Zone facade and interior lighting, party-room color, and entertainment-fixture maintenance. Unpublished specs are labeled as such.',
    h1: 'Commercial lighting projects',
    lede:
      'These pages describe only what the project files show. Missing fixture counts, wattages, and cities are left blank instead of estimated.'
  },
  guides: {
    updated: UPDATED,
    path: '/guides',
    title: 'Commercial Lighting Guides | Avision Lighting',
    description:
      'Reference notes from Avision Lighting on RGB vs RGBW, DMX addressing, LED retrofit evaluations, and South Florida exterior lighting conditions.',
    h1: 'Commercial lighting guides',
    lede:
      'Short buyer answers stay on the service pages. These guides are the longer versions of four topics, so the site has one place to cite for each.'
  }
};
